"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Plus, Search, X } from "lucide-react";

import { Button } from "@/app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog";
import { Input } from "@/app/components/ui/input";
import { cn } from "@/app/components/ui/utils";

export interface QuickAddSelectQuickAddProps<T> {
  triggerLabel?: string;
  dialogTitle: string;
  dialogDescription?: string;
  renderForm: (args: {
    onCreated: (record: T) => void;
    onCancel: () => void;
  }) => ReactNode;
}

export interface QuickAddSelectProps<T> {
  value: T | null;
  onChange: (record: T | null) => void;
  options: T[];
  getOptionValue: (option: T) => string | number;
  getOptionLabel: (option: T) => string;
  getOptionDescription?: (option: T) => string;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  requiredMessage?: string;
  errorId?: string;
  className?: string;
  quickAdd?: QuickAddSelectQuickAddProps<T>;
}

function normalizeText(value: string) {
  return value.trim().toLowerCase();
}

export function QuickAddSelect<T>({
  value,
  onChange,
  options,
  getOptionValue,
  getOptionLabel,
  getOptionDescription,
  searchQuery,
  onSearchQueryChange,
  isLoading = false,
  placeholder = "Select an item",
  disabled = false,
  required = false,
  requiredMessage,
  errorId,
  className,
  quickAdd,
}: QuickAddSelectProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState(searchQuery ?? "");

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentSearch = onSearchQueryChange ? searchQuery ?? "" : internalSearch;
  const setSearchQuery = onSearchQueryChange ?? setInternalSearch;

  const filteredOptions = useMemo(() => {
    if (onSearchQueryChange) return options;

    const normalized = normalizeText(currentSearch);
    if (!normalized) return options;

    return options.filter((option) => {
      const label = normalizeText(getOptionLabel(option));
      const description = getOptionDescription ? normalizeText(getOptionDescription(option)) : "";
      const optionValue = normalizeText(String(getOptionValue(option)));

      return (
        label.includes(normalized) ||
        description.includes(normalized) ||
        optionValue.includes(normalized)
      );
    });
  }, [currentSearch, getOptionDescription, getOptionLabel, getOptionValue, onSearchQueryChange, options]);

  const selectedLabel = value ? getOptionLabel(value) : "";
  const selectedValue = value ? String(getOptionValue(value)) : "";
  const displayValue = open ? currentSearch : value ? selectedLabel : currentSearch;

  useEffect(() => {
    inputRef.current?.dispatchEvent(new Event("change", { bubbles: true }));
  }, [selectedValue]);

  const closePicker = () => {
    setOpen(false);
    setSearchQuery("");
  };

  const handleSelect = (option: T) => {
    onChange(option);
    closePicker();
  };

  const handleOpenQuickAdd = () => {
    setOpen(false);
    setQuickAddOpen(true);
  };

  const handleCreated = (record: T) => {
    setQuickAddOpen(false);
    onChange(record);
    closePicker();
  };

  const handleCancelQuickAdd = () => {
    setQuickAddOpen(false);
    closePicker();
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-label={placeholder}
            placeholder={placeholder}
            value={displayValue}
            disabled={disabled}
            data-required={required ? "true" : undefined}
            data-required-empty={required && !value ? "true" : "false"}
            data-required-message={requiredMessage}
            aria-describedby={errorId}
            onFocus={() => {
              if (!disabled) {
                setOpen(true);
              }
            }}
            onClick={() => {
              if (!disabled) {
                setOpen(true);
              }
            }}
            onChange={(event) => {
              if (disabled) return;
              setOpen(true);
              setSearchQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closePicker();
              }
            }}
            className="pl-10"
          />
        </div>

        {value ? (
          <Button
            type="button"
            variant="ghost"
            className="shrink-0 px-3"
            aria-label="Clear selection"
            onClick={() => onChange(null)}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border bg-background shadow-md">
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <>
              <div className="max-h-64 overflow-auto">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No results found.</div>
                ) : (
                  filteredOptions.map((option) => {
                    const optionValue = String(getOptionValue(option));
                    const optionLabel = getOptionLabel(option);
                    const optionDescription = getOptionDescription?.(option);
                    const isSelected = value ? String(getOptionValue(value)) === optionValue : false;

                    return (
                      <button
                        key={optionValue}
                        type="button"
                        className="flex w-full items-start gap-3 px-3 py-2.5 text-left text-sm hover:bg-accent"
                        onClick={() => handleSelect(option)}
                      >
                        <Check className={cn("mt-0.5 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                        <div className="min-w-0">
                          <div className="truncate font-medium">{optionLabel}</div>
                          {optionDescription ? (
                            <div className="truncate text-xs text-muted-foreground">{optionDescription}</div>
                          ) : null}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {quickAdd ? (
                <div className="border-t p-2">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
                    onClick={handleOpenQuickAdd}
                  >
                    <Plus className="h-4 w-4" />
                    {quickAdd.triggerLabel ?? "+ New…"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {quickAdd ? (
        <Dialog
          open={quickAddOpen}
          onOpenChange={(nextOpen) => {
            if (nextOpen) {
              setQuickAddOpen(true);
            } else {
              handleCancelQuickAdd();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{quickAdd.dialogTitle}</DialogTitle>
              {quickAdd.dialogDescription ? (
                <DialogDescription>{quickAdd.dialogDescription}</DialogDescription>
              ) : null}
            </DialogHeader>
            {quickAdd.renderForm({
              onCreated: handleCreated,
              onCancel: handleCancelQuickAdd,
            })}
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
